import React, { useMemo, useEffect } from "react";
import { IAppRule } from "@Front/Api/Entities/rule";
import { useRouter } from "next/router";
import { Routes } from "@Front/Config/Routes";
import { useRolePermissionsLoading } from "@Front/Stores/RolePermissionsStore";
import { useAppRulePermissionCan } from "@Front/Hooks/useAppRulePermissionCan";

export enum RulesMode {
	OPTIONAL = "optional",
	NECESSARY = "necessary",
}

type IProps = {
	mode: RulesMode;
	rules: IAppRule[];
	children: React.ReactNode;
	isPage?: boolean;
};

type RulesPermissionCan = (name: IAppRule["name"], action: IAppRule["action"]) => boolean;

function computeRulesShowing(loading: boolean, mode: RulesMode, rules: IAppRule[], can: RulesPermissionCan): boolean {
	if (loading) {
		return false;
	}
	if (mode === RulesMode.NECESSARY) {
		return rules.every((rule) => can(rule.name, rule.action));
	}
	return (
		rules.length === 0 ||
		rules.some((rule) => {
			return can(rule.name, rule.action);
		})
	);
}

export default function Rules(props: IProps): React.ReactNode {
	const router = useRouter();
	const can = useAppRulePermissionCan();
	const loading = useRolePermissionsLoading();
	const isShowing = useMemo(
		() => computeRulesShowing(loading, props.mode, props.rules, can),
		[props.mode, props.rules, can, loading],
	);

	useEffect(() => {
		if (!props.isPage || loading) return;
		if (!isShowing) {
			router.push(Routes.pages.NotFound);
		}
	}, [isShowing, loading, props.isPage, props.rules, router]);

	if (loading) return null;
	if (!isShowing) {
		return null;
	}
	return props.children;
}
